import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { TableOrderButton } from '../buttons/TableOrderButton';
import { ColumnWithOrdering } from '../dashboard/ColumnWithOrdering';
import { SORT_DOWN, SORT_UP } from '../../utils/Array.extensions';
import { ColumnTitle } from '../dashboard/column_header/ColumnTitle';
import { FaIcon } from '../FaIcon';
import { FieldSensitivityModal } from './FieldSensitivityModal';
import { FieldHelpIcon } from '../elements/FieldHelpIcon';

class DataExplorerColumnHeaderComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            direction: this.props.defaultOrderingDirection || SORT_DOWN,
            isSensitivityModalOpen: false,
        };
        this.handleClick = this.handleClick.bind(this);
        this.handleSensitivityIconClick = this.handleSensitivityIconClick.bind(this);
        this.toggleSensitivityModal = this.toggleSensitivityModal.bind(this);
        this.handleSensitivitySaved = this.handleSensitivitySaved.bind(this);
    }

    handleClick(event) {
        event.preventDefault();
        if (!this.props.canBeSorted) {
            return;
        }

        // Ignorar clicks que ocurren justo después de un resize
        if (this._justResized) {
            return;
        }

        let columnWithOrdering = new ColumnWithOrdering(this.props.columnId, this.state.direction);
        if (this.props.isActive) {
            const newDirection = this._toggleDirection();
            columnWithOrdering = new ColumnWithOrdering(this.props.columnId, newDirection);
        }
        this.props.onSetOrdering(columnWithOrdering);
    }

    _toggleDirection() {
        let newDirection;
        if (this.state.direction === SORT_DOWN) {
            newDirection = SORT_UP;
        } else if (this.state.direction === SORT_UP) {
            newDirection = SORT_DOWN;
        } else {
            throw new Error(`Invalid direction state ${this.state.direction}`);
        }
        this.setState({direction: newDirection});
        return newDirection;
    }

    handleSensitivityIconClick(event) {
        event.stopPropagation(); // Evitar que se active el ordenamiento
        this.toggleSensitivityModal();
    }

    toggleSensitivityModal() {
        this.setState(prevState => ({
            isSensitivityModalOpen: !prevState.isSensitivityModalOpen
        }));
    }

    handleSensitivitySaved() {
        // Notificar al componente padre que se guardó la sensibilidad
        if (this.props.onSensitivitySaved) {
            this.props.onSensitivitySaved();
        }
    }

    /**
     * Determina si la columna es una métrica basándose en el prefijo del columnId
     */
    isMetricColumn() {
        const { columnId } = this.props;
        return columnId && columnId.startsWith('metric_');
    }

    /**
     * Extrae el fieldId del columnId
     * Para métricas: remueve el prefijo "metric_"
     * Para dimensiones: retorna el columnId directamente
     */
    getFieldId() {
        const { columnId } = this.props;
        if (this.isMetricColumn()) {
            return columnId.replace(/^metric_/, '');
        }
        return columnId;
    }

    renderSortingArrow() {
        if (this.props.canBeSorted) {
            return <TableOrderButton isActive={this.props.isActive}
                                     direction={this.state.direction}/>
        } else {
            return null;
        }
    }

    renderSensitivityIcon() {
        if (!this.isMetricColumn()) {
            return null;
        }

        const { t } = this.props;
        
        // Verificación defensiva: si t no está disponible, usar texto por defecto
        const titleText = t && typeof t === 'function' 
            ? t('data_explorer.configure_sensitivity', 'Configurar sensibilidad')
            : 'Configurar sensibilidad';

        return (
            <>
                <span 
                    onClick={this.handleSensitivityIconClick}
                    style={{ 
                        cursor: 'pointer',
                        color: '#6c757d',
                        fontSize: '0.9em',
                        marginLeft: '8px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: '1'
                    }}
                    title={titleText}
                >
                    <FaIcon iconName="cog" />
                </span>
                <FieldSensitivityModal
                    isOpen={this.state.isSensitivityModalOpen}
                    toggle={this.toggleSensitivityModal}
                    fieldId={this.getFieldId()}
                    fieldsData={this.props.fieldsData}
                    onSave={this.handleSensitivitySaved}
                />
            </>
        );
    }

    handleResizeMouseDown(event) {
        event.preventDefault();
        event.stopPropagation();
        // Marcar que estamos resizing para bloquear el click de ordenamiento
        this._justResized = true;
        // Limpiar el flag después de que el click event haya pasado
        const clearFlag = () => {
            setTimeout(() => { this._justResized = false; }, 0);
            document.removeEventListener('mouseup', clearFlag);
        };
        document.addEventListener('mouseup', clearFlag);
        if (this.props.onResizeStart) {
            this.props.onResizeStart(this.props.columnId, event);
        }
    }

    render() {
        const { columnId, title, canBeSorted, isActive, columnWidth, isResizing } = this.props;
        const isMetric = this.isMetricColumn();
        const fieldId = this.getFieldId();

        // Combinar iconos de ayuda y sensibilidad
        const additionalButtons = (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <FieldHelpIcon fieldId={fieldId} />
                {this.renderSensitivityIcon()}
            </span>
        );

        // Construir clases CSS
        const columnClass = isMetric ? 'data-explorer-metric-column' : 'data-explorer-dimension-column';
        const baseClasses = canBeSorted ? "dashboard-column-header header-ordering" : "dashboard-column-header";
        const finalClassName = `${baseClasses} ${columnClass}`;

        const thStyle = columnWidth ? { width: `${columnWidth}px`, minWidth: `${columnWidth}px`, maxWidth: `${columnWidth}px` } : {};

        return (
            <th
                className={finalClassName}
                onClick={this.handleClick}
                data-column-id={columnId}
                style={thStyle}
            >
                <ColumnTitle title={title || ''} additionalButton={additionalButtons} />
                {this.renderSortingArrow()}
                <div
                    className={`column-resize-handle ${isResizing ? 'resizing' : ''}`}
                    onMouseDown={(e) => this.handleResizeMouseDown(e)}
                    onClick={(e) => e.stopPropagation()}
                />
            </th>
        );
    }
}

export const DataExplorerColumnHeader = withTranslation()(DataExplorerColumnHeaderComponent);
